import { useState, useMemo } from 'react';
import { Bell, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

import { Button } from '@/shared/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Badge } from '@/shared/components/ui/badge';
import { useNotifications, type Notification } from '@/shared/hooks/use-notifications';
import { useNotificationStore } from '@/shared/store/notification-store';
import { cn } from '@/shared/lib/utils';

const severityIcon: Record<Notification['severity'], typeof AlertCircle> = {
  critical: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const severityColor: Record<Notification['severity'], string> = {
  critical: 'text-destructive',
  warning: 'text-amber-500',
  info: 'text-blue-500',
};

type CategoryFilter = 'all' | Notification['type'];

const CATEGORIES: { key: CategoryFilter; labelKey: string }[] = [
  { key: 'all', labelKey: 'notifications.categories.all' },
  { key: 'system', labelKey: 'notifications.categories.system' },
  { key: 'bot', labelKey: 'notifications.categories.bot' },
  { key: 'exchange', labelKey: 'notifications.categories.exchange' },
];

export function NotificationCenter() {
  const { t } = useTranslation('common');
  const { notifications, unreadCount } = useNotifications();
  const markSeen = useNotificationStore((s) => s.markSeen);
  const lastSeenAt = useNotificationStore((s) => s.lastSeenAt);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<CategoryFilter>('all');

  const filtered = useMemo(
    () => (category === 'all' ? notifications : notifications.filter((n) => n.type === category)),
    [notifications, category],
  );

  const isUnread = (n: Notification) => {
    if (!lastSeenAt) return true;
    return new Date(n.timestamp).getTime() > new Date(lastSeenAt).getTime();
  };

  return (
    <>
      <Button variant="ghost" size="icon" className="relative" onClick={() => setOpen(true)}>
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        <span className="sr-only">{t('notifications.title')}</span>
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-[400px] p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-sm">
                {t('notifications.title', 'Notifications')}
                {unreadCount > 0 && (
                  <Badge variant="destructive" className="ml-2 text-[10px]">
                    {unreadCount}
                  </Badge>
                )}
              </SheetTitle>
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto px-2 py-1 text-xs text-muted-foreground"
                  onClick={() => markSeen()}
                >
                  {t('notifications.markAllRead', 'Mark all read')}
                </Button>
              )}
            </div>
          </SheetHeader>

          {/* Category tabs */}
          <div className="flex gap-1 px-4 py-2 border-b">
            {CATEGORIES.map((cat) => {
              const count =
                cat.key === 'all'
                  ? notifications.length
                  : notifications.filter((n) => n.type === cat.key).length;
              return (
                <Button
                  key={cat.key}
                  variant={category === cat.key ? 'secondary' : 'ghost'}
                  size="xs"
                  className="h-7 text-xs"
                  onClick={() => setCategory(cat.key)}
                >
                  {t(cat.labelKey, cat.key)}
                  {count > 0 && <span className="ml-1 text-muted-foreground">({count})</span>}
                </Button>
              );
            })}
          </div>

          {/* Notification list */}
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-12 text-center flex-1">
              <Bell className="mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-sm font-medium text-muted-foreground">
                {t('notifications.noNotifications', 'No notifications')}
              </p>
              <p className="mt-1 text-xs text-muted-foreground/70">
                {t('notifications.allClear', 'All clear — nothing needs your attention')}
              </p>
            </div>
          ) : (
            <ScrollArea className="flex-1">
              <div className="divide-y">
                {filtered.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    unread={isUnread(notification)}
                    onNavigate={() => setOpen(false)}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function NotificationItem({
  notification,
  unread,
  onNavigate,
}: {
  notification: Notification;
  unread: boolean;
  onNavigate: () => void;
}) {
  const { t } = useTranslation('common');
  const Icon = severityIcon[notification.severity];
  const colorClass = severityColor[notification.severity];

  const relativeTime = formatDistanceToNow(new Date(notification.timestamp), {
    addSuffix: true,
  });

  const content = (
    <div
      className={cn(
        'flex gap-3 px-4 py-3 hover:bg-muted/50 transition-colors',
        unread && 'bg-muted/20',
      )}
    >
      <div className={cn('mt-0.5 shrink-0', colorClass)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium leading-tight">
            {t(notification.title, notification.tParams)}
          </p>
          {unread && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground leading-snug">
          {t(notification.message, notification.tParams)}
        </p>
        <div className="mt-1 flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] h-4 px-1">
            {notification.type}
          </Badge>
          <span className="text-[11px] text-muted-foreground/60">{relativeTime}</span>
        </div>
      </div>
    </div>
  );

  if (notification.link) {
    return (
      <Link to={notification.link} onClick={onNavigate} className="block">
        {content}
      </Link>
    );
  }

  return content;
}
